import { visit } from "unist-util-visit";
import type { Root } from "mdast";

/**
 * Maps the legal documents' callout directives onto portal styling.
 *
 * Deliberately separate from the help centre's remark-callouts, which looks
 * similar. The two vocabularies have different owners: help articles use what
 * this portal decides, while these directive names are part of the contract
 * published by @scryerlabs/legal and are rendered by the brochure site too.
 * Folding them into one map would mean a change made for a help article could
 * silently alter how a clause in the Terms is presented.
 *
 * Panels reuse the existing tinted-* classes rather than inventing a second
 * look, so a legal page reads as part of the portal.
 */
const VARIANTS: Record<string, string> = {
  // The ordinary emphasis panel, and by far the most common.
  warning: "legal-callout tinted-warning rounded p-3 mb-3",
  // An aside that qualifies the clause above it.
  note: "legal-callout tinted-info rounded p-3 mb-3",
  // Reserved for the risk-of-loss and arbitration notices. These should not
  // look like every other panel, or they stop being noticeable.
  danger: "legal-callout legal-callout-danger rounded p-3 mb-3",
  // The all-caps hypothetical-performance block.
  caps: "legal-callout legal-callout-caps rounded p-3 mb-3",
  // Address blocks.
  contact: "legal-callout legal-callout-contact rounded p-3 mb-3",
  // The tick and cross rows on Disclosures.
  check: "legal-mark legal-mark-check rounded p-3 mb-2",
  cross: "legal-mark legal-mark-cross rounded p-3 mb-2",
};

interface DirectiveNode {
  type: string;
  name?: string;
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
  };
}

export function remarkLegalCallouts() {
  return (tree: Root) => {
    visit(tree, (node) => {
      const directive = node as DirectiveNode;
      if (directive.type !== "containerDirective") return;

      const className = VARIANTS[directive.name ?? ""];
      // An unrecognised directive is left alone rather than silently swallowed.
      // It then renders as visible ":::whatever" text, which someone catches in
      // review — whereas swallowing it drops a clause out of a legal document.
      if (!className) return;

      directive.data = {
        ...directive.data,
        hName: "div",
        hProperties: { className: className.split(" ") },
      };
    });
  };
}
